// Call plugin.
import * as actionTypes from '../actionTypes'
import * as webrtcActionTypes from '../../../webrtc/interface/actionTypes'
import { CALL_STATES, CALL_DIRECTION } from '../../constants'
import { OPERATIONS, OP_STATUS } from '../constants'
import { setOperationState } from '../utils'

// Libraries.
import { combineActions, handleActions } from 'redux-actions'
import { concat, union } from 'lodash/fp'

/**
 * A no-op reducer.
 * Some reducers don't need to change state, but need to exist so their actions
 *    can be handled the same way as other actions (eg. wrapped to update local
 *    operation).
 * @method noop
 */
const noop = (state, action) => state

/**
 * The reducer logic used for finish operations that end a call.
 * @method callEnder
 */
const callEnder = (state, action) => {
  const now = Date.now()
  return {
    ...state,
    state: CALL_STATES.ENDED,
    // If there isn't a start time, then the call was never completed.
    //    Meaning it's duration was 0, so set the start time appropriately.
    startTime: state.startTime || now,
    endTime: now,
    remoteParticipant: {
      ...state.remoteParticipant,
      ...action.payload.remoteParticipant
    },
    // Closing the peer doesn't trigger an oniceconnectionstatechange event
    // although it does change the iceConnectionState property to closed
    mediaConnectionState: 'closed',
    // TODO: Remove this later.
    isPending: undefined,
    localTracks: [],
    remoteTracks: []
  }
}

// Top-tier reducers: Handles the whole call state.
const reducers = {}
// Call-tier reducers: Handles only a specific call's state.
const callReducers = {}

/*
 * Top-tier reducers.
 * Receives the entire call substate as state.
 */

// Add the new call to state.
reducers[actionTypes.MAKE_CALL] = {
  next (state, action) {
    // TODO: Call model? (with defaults for call state)
    let newCall = {
      id: action.payload.id,
      remoteParticipant: {
        displayNumber: action.payload.participantAddress,
        displayName: action.payload.participantName
      },
      // Number of the remote participant when the call was established.
      to: action.payload.participantAddress,
      from: action.payload.from,
      mediaConstraints: action.payload.mediaConstraints,
      dscpControls: action.payload.dscpControls,
      // TODO: Proper constants.
      state: CALL_STATES.INITIATING,
      direction: CALL_DIRECTION.OUTGOING,
      // Hold status.
      localHold: false,
      remoteHold: false,
      isCaller: true,
      // Flag indicating whether the call is anonymous or not
      isAnonymous: action.payload.isAnonymous,
      // The account token used to make the anonymous call
      account: action.payload.account,

      localTracks: [],
      remoteTracks: []
    }

    // Set the call's operation substate.
    newCall = setOperationState(newCall, action)

    return concat(state, newCall)
  }
}

// Add the new call to state.
reducers[actionTypes.CALL_INCOMING] = {
  next (state, action) {
    return concat(state, {
      // Dynamic state from the action.
      ...action.payload,

      // Constants for a new incoming call.
      direction: 'incoming',
      isCaller: false,
      // Initial call state.
      state: CALL_STATES.INITIATING,
      localHold: false,
      remoteHold: false,
      // No tracks available to render at first.
      localTracks: [],
      remoteTracks: []
    })
  }
}

/*
 * Call-tier reducers.
 * Receive a single call's state as state.
 */

callReducers[actionTypes.SEND_RINGING_FEEDBACK_FINISH] = {
  next (state, action) {
    return {
      ...state,
      state: CALL_STATES.RINGING
    }
  }
}

callReducers[actionTypes.CALL_RINGING] = {
  next (state, action) {
    if (action.payload.remoteParticipant) {
      return {
        ...state,
        state: CALL_STATES.RINGING,
        remoteParticipant: action.payload.remoteParticipant
      }
    } else {
      return {
        ...state,
        state: CALL_STATES.RINGING
      }
    }
  }
}

// Handle Early Media the same as Ringing, just with a different state.
callReducers[actionTypes.SESSION_PROGRESS] = {
  next (state, action) {
    return {
      ...state,
      state: CALL_STATES.EARLY_MEDIA,
      remoteParticipant: action.payload.remoteParticipant,
      remoteTracks: action.payload.remoteTracks
    }
  }
}

/*
 * Call-tier reducers.
 * Receive a single call's state as state.
 */

/*
 * Some reducers don't need to change state directly, but need to exist.
 * Call-specific reducers are wrapped to keep information about on-going
 *    operations up-to-date.
 */
callReducers[actionTypes.PENDING_OPERATION] = noop
callReducers[actionTypes.SEND_RINGING_FEEDBACK] = noop
callReducers[actionTypes.CALL_HOLD] = noop
callReducers[actionTypes.CALL_UNHOLD] = noop
callReducers[actionTypes.SEND_CUSTOM_PARAMETERS] = noop
callReducers[actionTypes.END_CALL] = noop
callReducers[actionTypes.ADD_MEDIA] = noop
callReducers[actionTypes.REMOVE_MEDIA] = noop
callReducers[actionTypes.ADD_BASIC_MEDIA] = noop
callReducers[actionTypes.REMOVE_BASIC_MEDIA] = noop
callReducers[actionTypes.SEND_DTMF] = noop
callReducers[actionTypes.SEND_DTMF_FINISH] = noop
callReducers[actionTypes.IGNORE_CALL] = noop
callReducers[actionTypes.DIRECT_TRANSFER] = noop
callReducers[actionTypes.REPLACE_TRACK] = noop
callReducers[actionTypes.REMOTE_SLOW_START] = noop
callReducers[actionTypes.GET_STATS] = noop
callReducers[actionTypes.GET_STATS_FINISH] = noop

/*
 * When answering, rejecting, or forwarding a call, update state to say that we
 *    are handling it.
 * This is needed for the WebRTC JS SDK because WebRTC Gateway will send a "call cancel" notification
 *    to _ALL_ of the user's subscriptions. The intention is to stop the call from
 *    ringing on other subscribed devices, and for the handling device to ignore
 *    the notification. This flag is used to know when to ignore the call cancel.
 */
const setHandling = (state, action) => {
  return {
    ...state,
    isHandling: true
  }
}
callReducers[actionTypes.ANSWER_CALL] = setHandling
callReducers[actionTypes.REJECT_CALL] = setHandling
callReducers[actionTypes.FORWARD_CALL] = setHandling

callReducers[actionTypes.CALL_CANCELLED] = {
  next (state, action) {
    return {
      ...state,
      state: CALL_STATES.CANCELLED
    }
  }
}

callReducers[actionTypes.IGNORE_CALL_FINISH] = {
  next (state, action) {
    // The call being ignored means it was not completed. Ensure there are times
    //    in state, and they reflect that the call was not completed.
    // TODO: Better call times.
    const now = Date.now()

    return {
      ...state,
      startTime: now,
      endTime: now,
      state: CALL_STATES.ENDED
    }
  }
}

callReducers[actionTypes.REJECT_CALL_FINISH] = {
  next (state, action) {
    // The call being rejected means it was not completed. Ensure there are
    //    times in state, and they reflect that the call was not completed.
    // TODO: Better call times.
    const now = Date.now()

    const newState = {
      ...state,
      startTime: now,
      endTime: now,
      state: CALL_STATES.ENDED
    }

    // After the reject operation finishes, remove the flag that indicates we
    //    were handling the call.
    delete newState.isHandling

    return newState
  }
}

callReducers[actionTypes.SESSION_CREATED] = {
  next (state, action) {
    // When we get SESSION_CREATED action, the call object already exists as part of reducers state
    // so we only add the webrtc session id. This way, when call:newTrack action is later triggered,
    // we can find the call object by searching for this associated webrtcSessionId.
    return {
      ...state,
      webrtcSessionId: action.payload.webrtcSessionId
    }
  }
}

callReducers[actionTypes.PENDING_MAKE_CALL] = {
  next (state, action) {
    return {
      ...state,
      state: CALL_STATES.INITIATED,
      wrtcsSessionId: action.payload.wrtcsSessionId,
      webrtcSessionId: action.payload.webrtcSessionId,
      bandwidth: action.payload.bandwidth,
      displayName: action.payload.displayName,
      customParameters: action.payload.customParameters,
      customBodies: action.payload.customBodies,
      localTracks: action.payload.localTracks
    }
  }
}

// Update the new call state based on the outcome.
callReducers[actionTypes.MAKE_CALL_FINISH] = {
  next (state, action) {
    return {
      ...state,
      ...action.payload
    }
  },
  throw (state, action) {
    return {
      ...state,
      ...action.payload
    }
  }
}

// Update the call state based on the outcome.
callReducers[actionTypes.ANSWER_CALL_FINISH] = {
  next (state, action) {
    // If the action has a Session ID, store it as the ID.
    // In some scenarios (slow start), the we only get an ID
    //    at the answer step.
    const webrtcId = action.payload.webrtcSessionId || state.webrtcSessionId

    const newState = {
      ...state,
      state: action.payload.state,
      mediaConstraints: action.payload.mediaConstraints,
      dscpControls: action.payload.dscpControls,
      webrtcSessionId: webrtcId,
      localHold: false,
      remoteHold: false,
      bandwidth: action.payload.bandwidth,
      customParameters: action.payload.customParameters,
      customBodies: action.payload.customBodies,
      localTracks: action.payload.localTracks,
      remoteTracks: action.payload.remoteTracks
    }

    // Add start time to the call's state here if the call is not a slowstart call
    if (action.meta && !action.meta.isSlowStart) {
      newState.startTime = action.payload.startTime
    }

    // If answering the Call puts us in Connected state (ie. not slow-start),
    //    then we can remove this flag since we know not to cancel a connected call.
    if (newState.state === CALL_STATES.CONNECTED) {
      delete newState.isHandling
    }

    return newState
  },
  throw (state, action) {
    const newState = {
      ...state,
      ...action.payload
    }

    // If we failed to answer the call, then we're not actually handling the
    //    call, so remove the flag.
    delete newState.isHandling

    return newState
  }
}

callReducers[actionTypes.CALL_ACCEPTED] = {
  next (state, action) {
    const newState = {
      ...state,
      ...action.payload
    }

    // If we were answering, but it's the remote accept that puts us in the
    //    Connected state (ie. slow-start), then we can remove this flag since
    //    we know not to cancel a connected call.
    if (newState.isHandling && newState.state === CALL_STATES.CONNECTED) {
      delete newState.isHandling
    }

    return newState
  },
  throw (state, action) {
    const newState = action.payload.state || state.state
    return {
      ...state,
      state: newState
    }
  }
}

// Handles both success and failure actions the same way.
// Instead of an object with `next` & `throw` properties, pass in a function.
// https://redux-actions.js.org/api/handleaction#handleactiontype-reducer-defaultstate
callReducers[actionTypes.END_CALL_FINISH] = callEnder

callReducers[actionTypes.UPDATE_CALL] = {
  next (state, action) {
    /*
     * Separate the action properties that should not be directly added to state.
     *  - transition: Data for an event; not call state.
     *  - localTracks: UPDATE_CALL should not affect local tracks.
     */
    const { transition, localTracks, remoteTracks, ...stateProperties } = action.payload

    const newState = {
      ...state,
      ...stateProperties
    }

    if (remoteTracks && remoteTracks.length > 0) {
      newState.remoteTracks = remoteTracks
    }

    /*
     * Edge-case workaround.
     * If the current operation is an on-going remote slow-start, then this remote
     *   update call action is intended to be the "finish" for it. The "update call"
     *   action being used here is an edge-case when the remote operation wasn't
     *   something we can identify.
     * This is being done manually because the "update call" action does not work
     *   with the SDK's "operation tracking" logic in the reducers.
     * See function `handleSlowUpdateResponse` in `/call/negotiation.js` for more info.
     * Reference: KJS-542, KJS-571
     */
    if (
      action.meta &&
      action.meta.isRemote === true &&
      newState.remoteOp &&
      newState.remoteOp.operation === OPERATIONS.SLOW_START &&
      newState.remoteOp.status === OP_STATUS.ONGOING
    ) {
      newState.remoteOp = undefined
    }

    return newState
  }
}

callReducers[actionTypes.CALL_HOLD_FINISH] = {
  next (state, action) {
    // Filter out the affected tracks from the list of remote tracks.
    //    `trackLeft` _should_ be an empty array at this point...
    const removedTracks = action.payload.remoteTracks
    const tracksLeft = state.remoteTracks.filter(trackId => !removedTracks.includes(trackId))

    // Filter out local tracks. It _should_ be an emptyy array at this point.
    // But only remove the tracks in the action since those are the tracks that
    //    will be in the "tracks removed" event.
    const { localTracks } = action.payload
    const localTracksLeft = state.localTracks.filter(trackId => !localTracks.includes(trackId))

    return {
      ...state,
      localHold: true,
      state: CALL_STATES.ON_HOLD,
      localTracks: localTracksLeft,
      remoteTracks: tracksLeft
    }
  }
}

callReducers[actionTypes.CALL_UNHOLD_FINISH] = {
  next (state, action) {
    // Add the affected tracks to the list of remote tracks.
    //    `state.remoteTracks` _should_ have been empty before this...
    const addedTracks = action.payload.remoteTracks
    const tracks = union(state.remoteTracks, addedTracks)

    const { localTracks } = action.payload
    const localAdded = union(state.localTracks, localTracks)

    const callState = state.remoteHold ? CALL_STATES.ON_HOLD : CALL_STATES.CONNECTED
    return {
      ...state,
      localHold: false,
      state: callState,
      localTracks: localAdded,
      remoteTracks: tracks
    }
  }
}

callReducers[actionTypes.SET_CUSTOM_PARAMETERS] = {
  next (state, action) {
    return {
      ...state,
      customParameters: action.payload.customParameters,
      customBodies: action.payload.customBodies
    }
  }
}

callReducers[actionTypes.SEND_CUSTOM_PARAMETERS_FINISH] = {
  next (state, action) {
    return {
      ...state,
      ...action.payload
    }
  }
}

callReducers[actionTypes.CALL_REMOTE_HOLD_FINISH] = {
  next (state, action) {
    // Filter out the affected tracks from the list of remote tracks.
    //    `trackLeft` _should_ be an empty array at this point...
    const removedRemote = action.payload.remoteTracks
    const tracksLeft = state.remoteTracks.filter(trackId => !removedRemote.includes(trackId))

    const removedLocal = action.payload.localTracks
    const localLeft = state.localTracks.filter(trackId => !removedLocal.includes(trackId))

    return {
      ...state,
      remoteHold: true,
      state: CALL_STATES.ON_HOLD,
      remoteParticipant: {
        ...state.remoteParticipant,
        ...action.payload.remoteParticipant
      },
      localTracks: localLeft,
      remoteTracks: tracksLeft
    }
  }
}

callReducers[actionTypes.CALL_REMOTE_UNHOLD_FINISH] = {
  next (state, action) {
    // Replace the remoteTracks with the remoteTracks affected by the unhold.
    const tracks = action.payload.remoteTracks

    const addedLocal = action.payload.localTracks
    const localTracks = union(state.localTracks, addedLocal)

    const callState = state.localHold ? CALL_STATES.ON_HOLD : CALL_STATES.CONNECTED
    const newCall = {
      ...state,
      remoteHold: false,
      state: callState,
      remoteParticipant: {
        ...state.remoteParticipant,
        ...action.payload.remoteParticipant
      },
      localTracks,
      remoteTracks: tracks
    }

    // If `hasMOH` was explicitly set to true, set it to false as part of unhold.
    // This will happen when there is an explicit "start MOH" negotiation but
    //    "stop MOH" is done as part of unhold.
    if (newCall.hasMOH === true) {
      newCall.hasMOH = false
    }

    return newCall
  }
}

callReducers[actionTypes.REMOTE_ADD_MEDIA_FINISH] = {
  next (state, action) {
    // Added the affected tracks to the list of remote tracks.
    const addedTracks = action.payload.remoteTracks
    const tracks = union(state.remoteTracks, addedTracks)

    return {
      ...state,
      remoteTracks: tracks
    }
  }
}
callReducers[actionTypes.REMOTE_REMOVE_MEDIA_FINISH] = {
  next (state, action) {
    // Filter out the affected tracks from the list of remote tracks.
    const removedTracks = action.payload.remoteTracks
    const tracksLeft = state.remoteTracks.filter(trackId => !removedTracks.includes(trackId))

    return {
      ...state,
      remoteTracks: tracksLeft
    }
  }
}

callReducers[actionTypes.REMOTE_START_MOH_FINISH] = {
  next (state, action) {
    // Added the affected tracks to the list of remote tracks.
    const addedTracks = action.payload.remoteTracks
    const tracks = union(state.remoteTracks, addedTracks)

    return {
      ...state,
      hasMOH: true,
      remoteTracks: tracks
    }
  }
}

callReducers[actionTypes.REMOTE_STOP_MOH_FINISH] = {
  next (state, action) {
    // Filter out the affected tracks from the list of remote tracks.
    const removedTracks = action.payload.remoteTracks
    const tracksLeft = state.remoteTracks.filter(trackId => !removedTracks.includes(trackId))

    return {
      ...state,
      hasMOH: false,
      remoteTracks: tracksLeft
    }
  }
}

callReducers[actionTypes.FORWARD_CALL_FINISH] = {
  next (state, action) {
    const newState = {
      ...state,
      state: CALL_STATES.ENDED
    }

    // After the forward operation finishes, remove the flag that indicates we
    //    were handling the call.
    delete newState.isHandling

    return newState
  }
}

callReducers[actionTypes.DIRECT_TRANSFER_FINISH] = {
  next: callEnder
}

reducers[actionTypes.CONSULTATIVE_TRANSFER] = {
  next (state, action) {
    const callIds = [action.payload.id, action.payload.otherCallId]

    // Iterate through the calls looking for the two being acted on.
    //    Update them to set the operation substate.
    return state.map(call => {
      if (callIds.includes(call.id)) {
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

reducers[actionTypes.PENDING_CONSULTATIVE_TRANSFER] = {
  next (state, action) {
    return state.map(call => {
      if (call.id === action.payload.id || call.id === action.payload.otherCallId) {
        // Update the local operation in the call's state.
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

reducers[actionTypes.CONSULTATIVE_TRANSFER_FINISH] = {
  next: (state, action) => {
    return state.map(call => {
      if (call.id === action.payload.id || call.id === action.payload.otherCallId) {
        const endedCall = callEnder(call, action)
        return setOperationState(endedCall, action)
      } else {
        return call
      }
    })
  },
  throw: (state, action) => {
    return state.map(call => {
      if (call.id === action.payload.id || call.id === action.payload.otherCallId) {
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

reducers[actionTypes.JOIN] = {
  next (state, action) {
    // Update operation substate state for both calls used in the operation.
    const callIds = [action.payload.id, action.payload.otherCallId]

    return state.map(call => {
      if (callIds.includes(call.id)) {
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

reducers[actionTypes.JOIN_START] = {
  next (state, action) {
    // Create the new joined
    let newCall = {
      id: action.payload.id,
      remoteParticipant: {
        displayNumber: action.payload.participantAddress,
        displayName: action.payload.participantName
      },
      // TODO: Have better names for `to` & `from`
      to: action.payload.participantAddress,
      from: action.payload.from,
      mediaConstraints: action.payload.mediaConstraints,
      state: CALL_STATES.INITIATING,
      direction: CALL_DIRECTION.OUTGOING,
      // Hold status.
      localHold: false,
      remoteHold: false,
      isCaller: true,
      isJoinedCall: true,
      customParameters: action.payload.customParameters,
      bandwidth: action.payload.bandwidth,
      dscpControls: action.payload.dscpControls,

      localTracks: [],
      remoteTracks: []
    }

    // Set the call's operation substate.
    newCall = setOperationState(newCall, action)

    return concat(state, newCall)
  }
}

reducers[actionTypes.PENDING_JOIN] = {
  next (state, action) {
    return state.map(call => {
      if (call.id === action.payload.id) {
        // Update the joined call with the new payload
        return {
          ...call,
          wrtcsSessionId: action.payload.wrtcsSessionId,
          webrtcSessionId: action.payload.webrtcSessionId,
          state: action.payload.state
        }
      } else if (action.payload.usedCallIds.includes(call.id)) {
        // Update the used calls' operation substates.
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

reducers[actionTypes.JOIN_FINISH] = {
  /*
   * JOIN_FINISH for a success scenario is triggered by receiving "sessionComplete" notifications
   * for the two initial calls that are joining (the action is dispatched with the initial calls' ids).
   * For this scenario, end these calls since the new joined call is made.
   */
  next: (state, action) => {
    return state.map(call => {
      if (
        call.id === action.payload.id ||
        (action.payload.usedCallIds && action.payload.usedCallIds.includes(call.id))
      ) {
        const endedCall = callEnder(call, action)
        return setOperationState(endedCall, action)
      } else {
        return call
      }
    })
  },
  throw: (state, action) => {
    return state.map(call => {
      // If this action was dispatched with the new call id, end the call
      // How to determine if it was dispatched with new call id:
      //  - The ID in the action is not in the `usedCallIds` of the action's payload
      // If there is no `usedCallIds`, then likely this action is dispatched with the unjoined call ID's
      // due to receiving a "sessionFailed" notification from the backend
      if (
        call.id === action.payload.id &&
        action.payload.usedCallIds &&
        !action.payload.usedCallIds.includes(call.id)
      ) {
        const endedCall = callEnder(call, action)
        return setOperationState(endedCall, action)
      } else if (
        call.id === action.payload.id ||
        (action.payload.usedCallIds && action.payload.usedCallIds.includes(call.id))
      ) {
        return setOperationState(call, action)
      } else {
        return call
      }
    })
  }
}

callReducers[actionTypes.ADD_MEDIA_FINISH] = {
  next (state, action) {
    const localAdded = action.payload.localTracks

    return {
      ...state,
      bandwidth: action.payload.bandwidth,
      localTracks: state.localTracks.concat(localAdded)
    }
  }
}

callReducers[actionTypes.REMOVE_MEDIA_FINISH] = {
  next (state, action) {
    const localRemoved = action.payload.localTracks
    const tracksLeft = state.localTracks.filter(trackId => !localRemoved.includes(trackId))

    return {
      ...state,
      bandwidth: action.payload.bandwidth,
      localTracks: tracksLeft
    }
  }
}

callReducers[actionTypes.REPLACE_TRACK_FINISH] = {
  next (state, action) {
    const oldTrackId = action.payload.oldTrackState.id
    const newTrackId = action.payload.newTrackId

    // Remove the old ID and add the new ID to localTracks state.
    const tracks = state.localTracks.filter(trackId => trackId !== oldTrackId).concat(newTrackId)

    return {
      ...state,
      localTracks: tracks
    }
  }
}

callReducers[webrtcActionTypes.SESSION_ICE_CONNECTION_STATE_CHANGE] = {
  next (state, action) {
    return {
      ...state,
      mediaConnectionState: action.payload.iceConnectionState
    }
  }
}
/*
 * Combine all of the call-tier reducers into a single reducer,
 *      each with a default state of empty object.
 */
const callReducer = handleActions(callReducers, {})

// Actions routed to call-tier reducers.
const specificCallActions = combineActions(
  actionTypes.PENDING_OPERATION,
  actionTypes.PENDING_MAKE_CALL,
  actionTypes.MAKE_CALL_FINISH,
  actionTypes.ANSWER_CALL,
  actionTypes.ANSWER_CALL_FINISH,
  actionTypes.REJECT_CALL,
  actionTypes.REJECT_CALL_FINISH,
  actionTypes.CALL_ACCEPTED,
  actionTypes.SEND_RINGING_FEEDBACK,
  actionTypes.SEND_RINGING_FEEDBACK_FINISH,
  actionTypes.CALL_RINGING,
  actionTypes.SESSION_PROGRESS,
  actionTypes.CALL_CANCELLED,
  actionTypes.IGNORE_CALL,
  actionTypes.IGNORE_CALL_FINISH,
  actionTypes.END_CALL,
  actionTypes.END_CALL_FINISH,
  actionTypes.CALL_HOLD,
  actionTypes.CALL_HOLD_FINISH,
  actionTypes.CALL_UNHOLD,
  actionTypes.CALL_UNHOLD_FINISH,
  actionTypes.SET_CUSTOM_PARAMETERS,
  actionTypes.SEND_CUSTOM_PARAMETERS,
  actionTypes.SEND_CUSTOM_PARAMETERS_FINISH,
  actionTypes.CALL_REMOTE_HOLD_FINISH,
  actionTypes.CALL_REMOTE_UNHOLD_FINISH,
  actionTypes.REMOTE_ADD_MEDIA_FINISH,
  actionTypes.REMOTE_REMOVE_MEDIA_FINISH,
  actionTypes.ADD_MEDIA,
  actionTypes.ADD_BASIC_MEDIA,
  actionTypes.ADD_MEDIA_FINISH,
  actionTypes.REMOVE_MEDIA,
  actionTypes.REMOVE_BASIC_MEDIA,
  actionTypes.REMOVE_MEDIA_FINISH,
  actionTypes.UPDATE_CALL,
  actionTypes.FORWARD_CALL,
  actionTypes.FORWARD_CALL_FINISH,
  actionTypes.DIRECT_TRANSFER,
  actionTypes.DIRECT_TRANSFER_FINISH,
  actionTypes.SEND_DTMF,
  actionTypes.SEND_DTMF_FINISH,
  actionTypes.JOIN,
  actionTypes.REPLACE_TRACK,
  actionTypes.REPLACE_TRACK_FINISH,
  actionTypes.MEDIA_RESTART,
  actionTypes.MEDIA_RESTART_FINISH,
  actionTypes.RESYNC,
  actionTypes.RESYNC_FINISH,
  actionTypes.REMOTE_SLOW_START,
  actionTypes.REMOTE_START_MOH_FINISH,
  actionTypes.REMOTE_STOP_MOH_FINISH,
  actionTypes.GET_STATS,
  actionTypes.GET_STATS_FINISH,
  actionTypes.SESSION_CREATED
)

const specificWebrtcSessionActions = combineActions(webrtcActionTypes.SESSION_ICE_CONNECTION_STATE_CHANGE)

/*
 * Reducer to handle specific call actions.
 * Routes the actions to the call-tier reducers.
 */
reducers[specificCallActions] = (state, action) => {
  return state.map(function (call) {
    // Only update the call related to the action.
    if (call.id === action.payload.id) {
      // Update call state as per the reducer.
      let updatedCall = callReducer(call, action)

      // Update the operation portion of the call state. This handles both
      //    successful and failure actions.
      updatedCall = setOperationState(updatedCall, action)
      return updatedCall
    } else {
      return call
    }
  })
}

/**
 * Reducer to handle specific webrtc actions.
 * Routes the actions to the call-tier reducers.
 */
reducers[specificWebrtcSessionActions] = (state, action) => {
  return state.map(function (call) {
    if (call.webrtcSessionId === action.payload.id) {
      return callReducer(call, action)
    } else {
      return call
    }
  })
}

/*
 * Combine all of top-tier reducers into a single reducer,
 *      each with a default state of an empty array.
 */
const reducer = handleActions(reducers, [])
export default reducer
